import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import IntakeForm from "@/pages/IntakeForm";
import MatterUploadPage from "@/pages/MatterUploadPage";
import VarFrameworkPage from "@/pages/VarFrameworkPage";
import FamilyLawPage from "@/pages/FamilyLawPage";
import EstatePage from "@/pages/EstatePage";
import ContactPage from "@/pages/ContactPage";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import NewMatter from "@/pages/portal/NewMatter";
import MatterDetail from "@/pages/portal/MatterDetail";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F3EF]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#0B1F3F] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-[#64748B] font-medium tracking-wide uppercase">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/virtual-asset-registry-framework" element={<VarFrameworkPage />} />
      <Route path="/family-law-digital-assets" element={<FamilyLawPage />} />
      <Route path="/estate-digital-asset-records" element={<EstatePage />} />
      <Route path="/estate-digital-asset-documentation" element={<Navigate to="/estate-digital-asset-records" replace />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/portal" element={<ProtectedRoute><PortalDashboard /></ProtectedRoute>} />
      <Route path="/portal/matters/new" element={<ProtectedRoute><NewMatter /></ProtectedRoute>} />
      <Route path="/portal/matters/:matterId" element={<ProtectedRoute><MatterDetail /></ProtectedRoute>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/intake" element={<ProtectedRoute><IntakeForm /></ProtectedRoute>} />
      <Route path="/upload-records" element={<ProtectedRoute><MatterUploadPage /></ProtectedRoute>} />
      <Route path="/matters/:matterId/upload" element={<ProtectedRoute><MatterUploadPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard/:tab" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
